import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'

export type ThemeName = 'default' | 'synto' | 'neo-brutal' | 'enterprise' | 'gogo' | 'soft-pastel' | 'enterprise-blue' | 'ai-matrix' | 'ns-design'
export type ThemeMode = 'light' | 'dark'
export type LayoutMode = 'sidebar' | 'topnav'

interface ThemeContextType {
    theme: ThemeName
    setTheme: (theme: ThemeName) => void
    mode: ThemeMode
    setMode: (mode: ThemeMode) => void
    toggleMode: () => void
    layoutMode: LayoutMode
    setLayoutMode: (mode: LayoutMode) => void
    containerWidth: 'standard' | 'full'
    setContainerWidth: (width: 'standard' | 'full') => void

    availableThemes: { id: ThemeName; name: string; description: string; colors: string[] }[]
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const AVAILABLE_THEMES: { id: ThemeName; name: string; description: string; colors: string[] }[] = [
    {
        id: 'default',
        name: 'Default',
        description: 'Clean, modern SaaS aesthetic with Slate & Blue.',
        colors: ['#f8fafc', '#0f172a', '#2563eb']
    },

    {
        id: 'neo-brutal',
        name: 'Neo-Brutalism',
        description: 'High contrast, bold borders, and hard shadows.',
        colors: ['#fff9c4', '#000000', '#ff6b6b']
    },
    {
        id: 'soft-pastel',
        name: 'Soft Pastel',
        description: 'Calm, friendly SaaS UI with soft shadows.',
        colors: ['#fdfbf7', '#8b5cf6', '#ec4899']
    },
    {
        id: 'enterprise-blue',
        name: 'Enterprise Blue',
        description: 'Professional, conservative dashboard for corporate tools.',
        colors: ['#f1f5f9', '#1e40af', '#1e3a8a']
    },
    {
        id: 'ai-matrix',
        name: 'AI Matrix',
        description: 'Modern AI-first product UI with data-driven look.',
        colors: ['#171717', '#10b981', '#059669']
    },
    {
        id: 'ns-design',
        name: 'NS Design',
        description: 'Pixel-perfect replica of NexLink Design System.',
        colors: ['#FDFDFF', '#5955D1', '#29294B']
    }
]

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    // Theme State
    const [theme, setThemeState] = useState<ThemeName>(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('nsui-theme') as ThemeName
            return (saved && AVAILABLE_THEMES.some(t => t.id === saved)) ? saved : 'default'
        }
        return 'default'
    })

    // Mode State — always light until dark mode is productized end-to-end.
    // Following prefers-color-scheme caused half-themed admin pages (tables/cards
    // went dark via CSS vars while most surfaces stayed bg-white).
    const [mode, setModeState] = useState<ThemeMode>('light')

    // Layout State
    const [layoutMode, setLayoutModeState] = useState<LayoutMode>(() => {
        if (typeof window !== 'undefined') {
            return (localStorage.getItem('nsui-layout-mode') as LayoutMode) || 'sidebar'
        }
        return 'sidebar'
    })

    // Container Width State
    const [containerWidth, setContainerWidthState] = useState<'standard' | 'full'>(() => {
        if (typeof window !== 'undefined') {
            return (localStorage.getItem('nsui-container-width') as 'standard' | 'full') || 'full'
        }
        return 'full'
    })

    const setTheme = useCallback((newTheme: ThemeName) => {
        setThemeState(prev => {
            if (newTheme === prev) return prev
            return newTheme
        })
    }, [])

    const setMode = useCallback((newMode: ThemeMode) => {
        setModeState(prev => {
            if (newMode === prev) return prev
            return newMode
        })
    }, [])

    const toggleMode = useCallback(() => {
        setModeState(prev => prev === 'dark' ? 'light' : 'dark')
    }, [])

    const setLayoutMode = useCallback((newLayout: LayoutMode) => {
        setLayoutModeState(prev => {
            if (newLayout === prev) return prev
            localStorage.setItem('nsui-layout-mode', newLayout)
            return newLayout
        })
    }, [])

    const setContainerWidth = useCallback((width: 'standard' | 'full') => {
        setContainerWidthState(prev => {
            if (width === prev) return prev
            localStorage.setItem('nsui-container-width', width)
            return width
        })
    }, [])

    // Effects
    // Sync theme to DOM immediately on mount/change
    useEffect(() => {
        const root = document.documentElement
        root.setAttribute('data-theme', theme)
        localStorage.setItem('nsui-theme', theme)
    }, [theme])

    useEffect(() => {
        const root = document.documentElement
        if (mode === 'dark') {
            root.classList.add('dark')
        } else {
            root.classList.remove('dark')
        }
        localStorage.setItem('nsui-theme-mode', mode)
    }, [mode])

    // Initialize DOM attributes synchronously to prevent flash
    // We can't do this inside the component body safely in all React versions, 
    // but we can rely on the useState initializer side-effect for specific client-side cases 
    // or just assume the useEffect is fast enough IF we don't block it.
    // The issue was likely the 'flash' of default content. 
    // Ideally this goes in index.html, but we'll try to ensure state is correct.

    const contextValue = useMemo(() => ({
        theme, setTheme,
        mode, setMode, toggleMode,
        layoutMode, setLayoutMode,
        containerWidth, setContainerWidth,
        availableThemes: AVAILABLE_THEMES
    }), [theme, setTheme, mode, setMode, toggleMode, layoutMode, setLayoutMode, containerWidth, setContainerWidth])

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme() {
    const context = useContext(ThemeContext)
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
